import React from 'react'

import Page from '../components/Page'
import LazyImage from '../components/LazyImage'

export default function TheServiceContinues (props) {
  const {
    tsc_intro_heading: introHeading,
    tsc_campaign_content: campaignContent,

    tsc_sponsors_heading: sponsorsHeading,
    tsc_sponsors: sponsors = [],

    tsc_activations_heading: activationsHeading,
    tsc_activations: activations = [],

    tsc_graphics: graphics = [],

    tsc_interest_form_url: interestFormUrl,
    tsc_donation_url: donationUrl
  } = props.acf || {}

  const hasIntro = introHeading || campaignContent
  const hasSponsors = sponsorsHeading || sponsors.length > 0
  const hasActivations = activationsHeading || activations.length > 0
  const hasGraphics = graphics.length > 0
  const hasClosingCta = interestFormUrl || donationUrl

  return (
    <Page
      title='The Service Continues'
      body={campaignContent}
      slug='/the-service-continues'
    >
      <main id='content'>
        <section className='service-title'>
          <div className='container'>
            <div className='row'>
              <div className='col-md-8 offset-md-2 text-center'>
                <h1 className='heading1'>The Service Continues</h1>
              </div>
            </div>
          </div>
        </section>

        {hasIntro && (
          <section className='service-intro'>
            <div className='container'>
              <div className='row'>
                <div className='col-md-8 offset-md-2'>
                  {introHeading && <h2 className='heading2'>{introHeading}</h2>}

                  {campaignContent && (
                    <div
                      className='service-intro__copy rich-text'
                      dangerouslySetInnerHTML={{
                        __html: campaignContent
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {hasSponsors && (
          <section className='service-sponsors'>
            <div className='container'>
              <div className='row'>
                <div className='col-md-8 offset-md-2'>
                  {sponsorsHeading && (
                    <h2 className='heading2'>{sponsorsHeading}</h2>
                  )}

                  {sponsors.length > 0 && (
                    <div className='row justify-content-center align-items-center service-sponsors__grid'>
                      {sponsors.map((sponsor, index) => {
                        const sponsorKey = sponsor.logo?.url || index
                        const logoImg = sponsor.logo?.url && (
                          <LazyImage
                            src={sponsor.logo.url}
                            alt={sponsor.logo.alt || ''}
                            imageClass='service-sponsors__image'
                          />
                        )

                        return (
                          <div
                            className='col-6 col-md-3 service-sponsors__logo'
                            key={sponsorKey}
                          >
                            {sponsor.link?.url ? (
                              <a
                                href={sponsor.link.url}
                                target={sponsor.link.target || '_blank'}
                                rel={
                                  sponsor.link.target === '_blank'
                                    ? 'noopener noreferrer'
                                    : undefined
                                }
                              >
                                {logoImg}
                              </a>
                            ) : (
                              logoImg
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {hasActivations && (
          <section className='service-activations'>
            <div className='container'>
              <div className='row'>
                <div className='col-md-8 offset-md-2'>
                  {activationsHeading && (
                    <h2 className='heading2'>{activationsHeading}</h2>
                  )}

                  <div className='service-activations__list'>
                    {activations.map((activation, index) => {
                      const activationKey = activation.event_name || index
                      const metaParts = [
                        activation.date,
                        activation.location
                      ].filter(Boolean)

                      return (
                        <div className='activation-item' key={activationKey}>
                          {activation.event_name && (
                            <h3 className='heading3'>
                              {activation.event_name}
                            </h3>
                          )}

                          {metaParts.length > 0 && (
                            <p className='activation-item__meta'>
                              {metaParts.join(' • ')}
                            </p>
                          )}

                          {activation.details && (
                            <div
                              className='activation-item__details rich-text'
                              dangerouslySetInnerHTML={{
                                __html: activation.details
                              }}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {hasGraphics && (
          <section className='service-graphics'>
            <div className='container'>
              <div className='service-graphics__stack'>
                {graphics.map((graphic, index) => {
                  const graphicKey = graphic.image?.url || index
                  return graphic.image?.url ? (
                    <div className='service-graphics__item' key={graphicKey}>
                      <LazyImage
                        src={graphic.image.url}
                        alt={graphic.image.alt || ''}
                        imageClass='service-graphics__image'
                      />
                    </div>
                  ) : null
                })}
              </div>
            </div>
          </section>
        )}

        {hasClosingCta && (
          <aside className='service-cta'>
            <div className='cta-banner d-flex flex-column justify-content-center align-items-center'>
              <h2 className='heading1'>Get Involved</h2>

              <div className='cta-banner__link service-cta__links'>
                {interestFormUrl && (
                  <a
                    href='mailto:sblardie@thequellfoundation.org'
                    className='link'
                    target='_self'
                  >
                    Explore a Sponsorship
                  </a>
                )}

                {donationUrl && (
                  <a href={donationUrl} className='link' target='_self'>
                    Donate
                  </a>
                )}
              </div>
            </div>
          </aside>
        )}
      </main>

      <style jsx>{`
        /* Single source of vertical rhythm per gap: only margin-top, on the
           section that follows — no margin-bottom, no inner-wrapper margins,
           no min-height. Matches the real site rhythm measured live against
           /scholarships/ (80px between repeated content rows). */

        .service-title {
          margin-top: 60px;
        }

        .service-intro {
          margin-top: 50px;
        }

        .service-sponsors,
        .service-activations,
        .service-graphics {
          margin-top: 80px;
        }

        .service-graphics {
          margin-bottom: 80px;
        }

        @media (max-width: 991px) {
          .service-title {
            margin-top: 40px;
          }

          .service-intro {
            margin-top: 30px;
          }

          .service-sponsors,
          .service-activations,
          .service-graphics {
            margin-top: 50px;
          }

          .service-graphics {
            margin-bottom: 50px;
          }
        }

        .service-intro h2,
        .service-sponsors h2,
        .service-activations h2 {
          margin-bottom: 20px;
        }

        .service-sponsors__grid {
          row-gap: 30px;
        }

        .service-sponsors__logo {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 150px;
        }

        .service-sponsors__logo a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        @media (max-width: 767px) {
          .service-sponsors__logo {
            height: 110px;
          }
        }

        .activation-item {
          padding: 20px 0;
          border-bottom: 1px solid #4e87a0;
        }

        .activation-item:first-child {
          padding-top: 0;
        }

        .activation-item__meta {
          margin-top: 8px;
          margin-bottom: 0;
          font-weight: bold;
          color: #3a434f;
        }

        .activation-item__details {
          margin-top: 15px;
        }

        .service-graphics__stack {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .service-graphics__item {
          margin-bottom: 30px;
        }

        .service-graphics__item:last-child {
          margin-bottom: 0;
        }

        .service-cta__links {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 20px;
        }

        .rich-text :global(p) {
          margin-bottom: 1em;
        }

        .rich-text :global(ul),
        .rich-text :global(ol) {
          margin-bottom: 1em;
          padding-left: 1.5em;
        }

        .rich-text :global(a) {
          color: #074a76;
          text-decoration: underline;
        }

        :global(.service-sponsors__image) {
          height: 110px;
          width: auto;
          max-width: 100%;
          object-fit: contain;
        }

        @media (max-width: 767px) {
          :global(.service-sponsors__image) {
            height: 75px;
          }
        }

        :global(.service-graphics__image) {
          width: 100%;
          max-width: 591px;
          height: auto;
          display: block;
          margin: 0 auto;
        }
      `}</style>
    </Page>
  )
}
